import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react'
import { useAuthOptional } from '../../contexts/authContextRaw'
import type { ExecuteNodeAnalytics } from '../../analytics/nodeAnalytics'
import {
  acknowledgeGenerationSubmission,
  activateGenerationGracePeriod,
  cancelGenerationGracePeriod,
  clearGenerationGraceFailure,
  generationGraceRequestKey,
  getGenerationGraceRecord,
  scheduleGenerationGracePeriod,
  subscribeGenerationGraceRecord,
  type GenerationGraceFailure,
  type GenerationGracePhase,
  type GracePeriodNodeType,
} from './generationGracePeriodStore'

const LOCAL_DEBUG_OWNER_ID = 'local-debug-user'

function ownerIdForRuntime(
  auth: ReturnType<typeof useAuthOptional>,
): string | null {
  if (auth?.user?.id) return auth.user.id
  return import.meta.env.DEV ? LOCAL_DEBUG_OWNER_ID : null
}

/**
 * App-level timer restorer. It is intentionally non-visual and lives under the
 * AuthProvider, so a reload resumes pending commands even when their ReactFlow
 * nodes are outside the viewport and therefore not mounted.
 */
export function GenerationGracePeriodRuntime() {
  const auth = useAuthOptional()
  const ownerId = ownerIdForRuntime(auth)

  useEffect(() => {
    if (auth?.isLoading) return
    activateGenerationGracePeriod(ownerId)
    return () => activateGenerationGracePeriod(null)
  }, [auth?.isLoading, ownerId])

  return null
}

export interface UseGenerationGracePeriodOptions {
  projectId: string | undefined
  canvasId: string
  nodeId: string
  nodeType: GracePeriodNodeType
  isBackendRunning: boolean
}

export interface UseGenerationGracePeriodResult {
  phase: GenerationGracePhase | null
  isWaiting: boolean
  isSubmitting: boolean
  isLocallyGenerating: boolean
  failure: GenerationGraceFailure | null
  schedule: (
    count?: number,
    analytics?: ExecuteNodeAnalytics,
    options?: { closingFrameHandshake?: boolean },
  ) => boolean
  cancelPending: () => boolean
  clearFailure: () => void
}

export function useGenerationGracePeriod({
  projectId,
  canvasId,
  nodeId,
  nodeType,
  isBackendRunning,
}: UseGenerationGracePeriodOptions): UseGenerationGracePeriodResult {
  const auth = useAuthOptional()
  const ownerId = ownerIdForRuntime(auth)
  const key = useMemo(
    () =>
      ownerId && projectId
        ? generationGraceRequestKey(ownerId, projectId, canvasId, nodeId)
        : null,
    [canvasId, nodeId, ownerId, projectId],
  )

  const subscribe = useCallback(
    (listener: () => void) =>
      key === null ? () => {} : subscribeGenerationGraceRecord(key, listener),
    [key],
  )
  const getSnapshot = useCallback(
    () => (key === null ? null : getGenerationGraceRecord(key)),
    [key],
  )
  const record = useSyncExternalStore(subscribe, getSnapshot, () => null)

  // Once YDoc reports the real backend run, the browser-only handoff record is
  // no longer needed. Clearing it also retires a restored `submitting` record
  // whose original page was torn down before its keepalive promise resolved.
  useEffect(() => {
    if (key !== null && isBackendRunning) acknowledgeGenerationSubmission(key)
  }, [isBackendRunning, key])

  const schedule = useCallback(
    (
      count: number = 1,
      analytics?: ExecuteNodeAnalytics,
      options?: { closingFrameHandshake?: boolean },
    ) => {
      if (!ownerId) {
        throw new Error('Cannot schedule generation without an authenticated user')
      }
      if (!projectId) {
        throw new Error('Cannot schedule generation without a project ID')
      }
      return scheduleGenerationGracePeriod({
        ownerId,
        projectId,
        canvasId,
        nodeId,
        nodeType,
        count,
        ...(options?.closingFrameHandshake ? { closingFrameHandshake: true } : {}),
        ...(analytics ? { analytics } : {}),
      })
    },
    [canvasId, nodeId, nodeType, ownerId, projectId],
  )

  const cancelPending = useCallback(
    () => (key === null ? false : cancelGenerationGracePeriod(key)),
    [key],
  )
  const clearFailure = useCallback(() => {
    if (key !== null) clearGenerationGraceFailure(key)
  }, [key])

  return {
    phase: record?.phase ?? null,
    isWaiting: record?.phase === 'waiting',
    isSubmitting: record?.phase === 'submitting',
    isLocallyGenerating: record?.phase === 'waiting' || record?.phase === 'submitting',
    failure: record?.failure ?? null,
    schedule,
    cancelPending,
    clearFailure,
  }
}
